import { CircleNotchIcon } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type AdminUserDetail, apiErrorDetail, useAdminUser, useUpdateUser } from "@/lib/api";
import { logger } from "@/lib/logger";
import type { Role } from "@/lib/roles";

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserEditDialog({ userId, open, onOpenChange }: Props) {
  const userQuery = useAdminUser(open ? userId : null);
  const user = userQuery.data;
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        {userQuery.isError ? (
          <>
            <p className="text-destructive py-8 text-center text-sm">
              Failed to load user details.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        ) : userQuery.isLoading || !user ? (
          <div className="flex justify-center py-8">
            <CircleNotchIcon className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : (
          // Keyed on the loaded record, so the form re-initialises from fresh
          // data instead of syncing props into state.
          <UserEditForm key={`${user.id}:${user.updated_at}`} user={user} onDone={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface UserEditFormProps {
  user: AdminUserDetail;
  onDone: () => void;
}

function UserEditForm({ user, onDone }: UserEditFormProps) {
  const updateMutation = useUpdateUser();
  const [firstName, setFirstName] = useState(user.first_name ?? "");
  const [lastName, setLastName] = useState(user.last_name ?? "");
  const [role, setRole] = useState<Role>(user.role);
  const [error, setError] = useState("");
  // Roles follow the email domain: BOI accounts are staff or admin, everyone
  // else is a client. A client's role is therefore not editable here.
  const isClient = user.role === "client";

  const handleRoleChange = (value: string) => {
    if (value === "staff" || value === "admin") {
      setRole(value);
    }
  };

  const handleSave = () => {
    setError("");
    updateMutation.mutate(
      {
        userId: user.id,
        firstName,
        lastName,
        role: role !== user.role ? role : undefined,
      },
      {
        onSuccess: onDone,
        onError: (err) => {
          const detail = apiErrorDetail(err);
          logger.debug("[admin] update user failed:", detail);
          setError(updateErrorMessage(detail));
        },
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <p className="text-muted text-sm">{user.email}</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="editFirstName" className="text-foreground text-sm font-medium">
              First name
            </label>
            <Input
              id="editFirstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="editLastName" className="text-foreground text-sm font-medium">
              Last name
            </label>
            <Input
              id="editLastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        {isClient ? (
          <div className="space-y-1">
            <p className="text-foreground text-sm font-medium">Role</p>
            <p className="text-foreground text-sm">Client</p>
            <p className="text-muted text-xs">
              Client accounts stay clients. BOI staff get their role by signing in with Google.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="editRole" className="text-foreground text-sm font-medium">
              Role
            </label>
            <select
              id="editRole"
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
              className="border-input bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </DialogFooter>
    </>
  );
}

function updateErrorMessage(detail: string | undefined): string {
  if (detail === "role_domain_mismatch") {
    return "That role doesn't fit this account: BOI emails are staff or admin, everyone else is a client.";
  }
  if (detail === "Cannot demote yourself") {
    return "You can't remove your own admin role.";
  }
  return "Failed to update user.";
}
